import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { fmtMoney } from '../../shared/format';
import { Badge, Card, Empty, ErrorBox, Loading, useAsync } from '../../shared/ui';
import { api } from '../api';
import { CompanyCifs } from '../components/CompanyCifs';
import { NewTenantModal } from '../components/NewTenantModal';
import { PageHead } from '../components/PageHead';
import { SubscriptionBadge } from '../components/SubscriptionBadge';
import type { AdminTenant } from '../types';

export function TenantsPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const tenants = useAsync(() => api.get<{ tenants: AdminTenant[] }>('/admin/tenants').then((r) => r.tenants), []);
  const creating = params.get('nuevo') === '1';

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = tenants.data ?? [];
    return q ? all.filter((t) => `${t.name} ${t.slug} ${t.hubClient}`.toLowerCase().includes(q)) : all;
  }, [tenants.data, query]);

  // Dominio de los portales a partir de uno ya dado de alta; si no hay ninguno, el de producción.
  const domain = tenants.data?.[0]?.portalUrl.replace(/^https?:\/\/[^.]+\./, '') ?? 'provalia.ecinsapp.es';

  return (
    <div className="stack lg">
      <PageHead
        title="Clientes"
        description="Cada cliente tiene su portal en su propio subdominio, su túnel del hub y sus sociedades de SAP."
        actions={
          <button className="btn primary" onClick={() => setParams({ nuevo: '1' })}>
            Nuevo cliente
          </button>
        }
      />

      <Card
        bodyless
        title={<h2>Todos los clientes {tenants.data ? <span className="subtle">({tenants.data.length})</span> : null}</h2>}
        actions={
          tenants.data && tenants.data.length > 4 ? (
            <input
              className="input sm search"
              type="search"
              placeholder="Buscar por nombre, identificador o túnel"
              aria-label="Buscar clientes"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          ) : null
        }
      >
        {tenants.loading && !tenants.data ? (
          <Loading />
        ) : tenants.error ? (
          <div className="card-body">
            <ErrorBox error={tenants.error} onRetry={() => void tenants.reload()} />
          </div>
        ) : !tenants.data?.length ? (
          <Empty icon="＋">
            <p>Aún no hay ningún cliente.</p>
            <p className="subtle">Pulsa «Nuevo cliente» para dar de alta el primero.</p>
          </Empty>
        ) : list.length === 0 ? (
          <Empty icon="⌕">Ningún cliente coincide con «{query}».</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Portal</th>
                  <th>Túnel</th>
                  <th>Suscripción</th>
                  <th className="num">Cuota</th>
                  <th>Sociedades y CIF</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className="clickable" onClick={() => navigate(`/clientes/${encodeURIComponent(t.slug)}`)}>
                    <td>
                      <Link to={`/clientes/${encodeURIComponent(t.slug)}`} className="strong-link" onClick={(e) => e.stopPropagation()}>
                        {t.name}
                      </Link>
                      <div className="mono subtle">{t.slug}</div>
                    </td>
                    <td>
                      <a href={t.portalUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                        {t.portalUrl.replace(/^https?:\/\//, '')}
                      </a>
                    </td>
                    <td className="nowrap">{t.hubClient}</td>
                    <td>
                      <div className="row">
                        <SubscriptionBadge status={t.subscription.status} />
                        {!t.active ? <Badge>Inactivo</Badge> : null}
                      </div>
                    </td>
                    <td className="num nowrap">{t.subscription.monthlyFee !== null ? fmtMoney(t.subscription.monthlyFee) : '—'}</td>
                    <td>
                      <CompanyCifs companies={t.companies} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating ? (
        <NewTenantModal
          domain={domain}
          onClose={() => setParams({})}
          onCreated={(slug) => navigate(`/clientes/${encodeURIComponent(slug)}`)}
        />
      ) : null}
    </div>
  );
}
